// src/views/Users/Customers/Modal/AddProjectModal.jsx
import React, { useState, useEffect, useRef } from "react";
import { Modal, Button } from "react-bootstrap";
import { globalConstants, modeConstants } from "../../constants";
import ActionsDataTable from "../DataTable/ActionsDataTable";
import { useNavigate } from "react-router-dom";
import AddProjectModal from "./ProjectModal";
import AddCandidateModal from "./CandidateModal";
import ViewAllCandidatesModal from "./ViewAllCandidatesModal";
import { useDispatch, useSelector } from "react-redux";
import {
  candidateActions,
  companyActions,
  projectActions,
  recruiterActions,
  screenActions,
} from "../../redux/actions";
import { SCREENS_IDS } from "../../system";

export default function AllProjectModal({ show, onHide, company }) {
  const initialGridState = {
    length: 10,
    pageNumber: 1,
    orderBy: "",
    filterQuery: null,
    search: null,
    filterExpression: globalConstants.FILTER_EXPRESSION,
  };

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const sortingInProgressRef = useRef(false);

  const [gridState, setGridState] = useState(initialGridState);
  const [defaultPage, setDefaultPage] = useState(initialGridState.pageNumber);
  const [rowData, setRowData] = useState({ items: [], total: 0 });
  const allData = useSelector((s) => s.projectReducer.data);
  let isManager = useSelector((s) => s.recruiterReducer.isManager);
  // modal + row state (for Edit/View/Add Project)
  const [mode, setMode] = useState(modeConstants.ADD_MODE);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showAddProject, setShowAddProject] = useState(false);
  const [showAddCan, setShowAddCan] = useState(false);
  const [showViewCan, setShowViewCan] = useState(false);
  const companyData = useSelector((s) => s.companyReducer.formData);
  // table columns

  const actions =
  {
    name: "Actions",
    sortable: false,
    width: "170px",
    cell: (row) => (
      <div
        className="d-flex align-items-center gap-2"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="btn btn-sm btn-outline-primary"
          title="Edit"
          aria-label="Edit"
          onClick={() => {
            getRowData(row, modeConstants.UPDATE_MODE);
            setShowAddProject(true);
          }}
        >
          <i className="bi bi-pencil" />
        </button>

        <button
          type="button"
          className="btn btn-sm btn-outline-success"
          title="View"
          aria-label="View"
          onClick={() => {
            getRowData(row, modeConstants.VIEW_MODE);
            setShowAddProject(true); // reuse modal as view demo
          }}
        >
          <i className="bi bi-eye" />
        </button>

        <div className="dropdown">
          <button
            className="btn btn-sm btn-outline-secondary"
            type="button"
            data-bs-toggle="dropdown"
            aria-expanded="false"
            title="More"
          >
            <i className="bi bi-three-dots-vertical" />
          </button>
          <ul
            className="dropdown-menu dropdown-menu-end"
            onClick={(e) => e.stopPropagation()}
          >
            {!isManager && (
              <li>
                <button
                  className="dropdown-item"
                  onClick={() => {
                    getRowData(row, modeConstants.ADD_MODE);
                    setShowAddCan(true);
                  }}
                >
                  <i className="bi bi-plus-circle me-2" /> Add Candidate
                </button>
              </li>
            )}

            <li>
              <button
                className="dropdown-item"
                onClick={() => {
                  getCandidateByProjectId(row, modeConstants.ADD_MODE);
                  setShowViewCan(true);
                }}
              >
                <i className="bi bi-collection me-2" /> View All Candidates
              </button>
            </li>
          </ul>
        </div>
      </div>
    ),
    ignoreRowClick: true,
    allowOverflow: true,
    button: true,
  };

  const getRowData = (row, nextMode) => {
    dispatch(projectActions.getProjectById(row.id));
    setMode(nextMode);
  };

  const toggleStatus = (id) => {
    dispatch(projectActions.deleteProjectById(id, companyData?.id));
  };

  const getCandidateByProjectId = (row, nextMode) => {
    dispatch(projectActions.getProjectById(row.id));
    dispatch(candidateActions.getAllCandidate(initialGridState, row.id));
    setMode(nextMode);
  };

  const onSort = (sortedColumn, sortDirection) => {
    sortingInProgressRef.current = true;
    const columnDirection = sortDirection
      ? sortDirection.toUpperCase() // "ASC" | "DESC"
      : null;
    setDefaultPage(gridState.pageNumber);
    setGridState((prevState) => ({
      ...prevState,
      orderBy:
        columnDirection !== null ? `${sortedColumn} ${columnDirection}` : "",
    }));
  };

  const onChangePage = (pageNumber) => {
    if (!sortingInProgressRef.current) {
      setGridState((prevState) => ({
        ...prevState,
        pageNumber: pageNumber,
      }));
    }
  };

  const onChangeRowsPerPage = (newPerPage, pageNumber) => {
    setGridState((prevState) => ({
      ...prevState,
      length: newPerPage,
      pageNumber: pageNumber,
    }));
  };

  const getAll = () => {
    if (companyData?.id) {
      dispatch(projectActions.getAllProject(gridState, companyData?.id));
    }
  };
  useEffect(() => {
    if (allData !== null) {
      setRowData(allData);
    }
    // eslint-disable-next-line
  }, [allData]);

  useEffect(() => {
    getAll();
    sortingInProgressRef.current = false;
    // eslint-disable-next-line
  }, [gridState]);

  useEffect(() => {
    dispatch(companyActions.getAllCompanyList());
    dispatch(recruiterActions.getAllRecruiterList());
    dispatch(projectActions.getAllProjectList());
  }, []);

  useEffect(() => {
    if (!showViewCan) {
      dispatch(screenActions.getColumnsByScreenId(SCREENS_IDS.ALL_PROJECTS));
      dispatch(screenActions.getUserPreferencesByScreenId(SCREENS_IDS.ALL_PROJECTS));
    }
  }, [showViewCan]);

  return (
    <Modal show={show} onHide={onHide} size="lg" backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title>View Company Projects</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <AddProjectModal
          show={showAddProject}
          onHide={() => setShowAddProject(false)}
          mode={mode}
        />

        <AddCandidateModal
          show={showAddCan}
          onHide={() => setShowAddCan(false)}
          company={selectedRow}
          mode={mode}
        />

        {showViewCan ? (
          <ViewAllCandidatesModal
            show={showViewCan}
            onHide={() => setShowViewCan(false)}
            company={selectedRow}
          />
        ) : ""}

        <ActionsDataTable
          actions={actions}
          data={rowData.items}
          pagination
          highlightOnHover
          striped
          responsive
          persistTableHead
          selectableRows
          selectableRowsHighlight
          selectableRowsVisibleOnly
          paginationServer
          onSort={onSort}
          gridState={gridState}
          paginationTotalRows={rowData.total}
          onChangeRowsPerPage={onChangeRowsPerPage}
          onChangePage={onChangePage}
          paginationDefaultPage={defaultPage}
          paginationResetDefaultPage
          setMode={setMode}
          setShowModal={setShowAddProject}
          setGridState={setGridState}
          toggleStatus={toggleStatus}
          screenId={SCREENS_IDS.ALL_PROJECTS}
        />
      </Modal.Body>
    </Modal>
  );
}
